import { Injectable, Inject, InjectionToken } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Observable } from 'rxjs';

import { Store, StoreReadonly } from '@skyeng/libs/shared/store';

import { IRequestParams, ISortCriteria, IPager } from '../models/interfaces';
import { IListManagerStoreParams } from '../models/interfaces';
import { defaultStoreParams } from '../configs';

export const ListManagerInitialState = new InjectionToken<IListManagerStoreParams>(
  'app.shared.services.list-manager.intial-state',
  {
    providedIn: 'root',
    factory: () => {
      return {
        ...defaultStoreParams,
        filter: null,
      };
    },
  },
);

@Injectable({
  providedIn: 'root',
})
export class ListManagerService {
  store$: StoreReadonly<IListManagerStoreParams>;
  private store: Store<IListManagerStoreParams>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    @Inject(ListManagerInitialState) private storeInitialState: IListManagerStoreParams,
  ) {
    this.store = new Store<IListManagerStoreParams>(storeInitialState);
    this.store$ = this.store.getReadonly();
    this.activatedRoute.queryParams.pipe().subscribe(params => {
      this.handleQueryParams(params);
    });
  }

  params(): Observable<Params> {
    return this.activatedRoute.queryParams;
  }

  hasFilters(): boolean {
    return JSON.stringify(this.store$.get('filter')) !== JSON.stringify(this.storeInitialState.filter);
  }

  resetFilters(): void {
    this.resetFilterState();
    this.updateQueryParams();
  }

  handlePage(event: PageEvent): void {
    const nextPage = event.pageIndex + 1;
    this.updatePagerState({ page: nextPage });
  }

  onSort(sort: ISortCriteria): void {
    this.updateSortCriteriaState(sort);
    this.updateQueryParams();
  }

  onFilter<T>(filter: T): void {
    /*
      Rewind pager to the beginning because we change filter
       and don't know how many pages will be recieve
    */

    this.resetPagerState();

    this.updateFilterState(filter);
    this.updateQueryParams();
  }

  getListParams(): IRequestParams {
    const sortCriteria = this.store.get('sortCriteria');
    const pager = this.store.get('pager');
    const filter = this.store.get('filter');

    return {
      page: pager.page,
      count: pager.count,
      sortCriteria: sortCriteria ? JSON.stringify(sortCriteria) : null,
      filterCriteria: filter ? JSON.stringify(filter) : null,
    } as IRequestParams;
  }

  updatePagerState(value: Partial<IPager>, updateQueryParams = true): void {
    const pager = this.store.get('pager');
    this.store.updateField('pager', { ...pager, ...value });
    if (updateQueryParams) {
      this.updateQueryParams();
    }
  }

  updateFilterState<T>(value: Partial<T>): void {
    const filter = this.store.get('filter');
    const newState = filter ? { ...filter, ...value } : { ...value };
    this.store.updateField('filter', newState as T);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleQueryParams(params: { [x: string]: any }): void {
    const filterParamData = params['filter'];
    if (filterParamData) {
      const filter = JSON.parse(filterParamData);
      if (filter) {
        this.updateFilterState({ ...this.storeInitialState.filter, ...filter });
      }
    } else {
      this.resetFilterState();
    }

    const pager: IPager = {
      count: params['count'] || this.storeInitialState.pager.count,
      page: params['page'] || this.storeInitialState.pager.page,
      total: this.store$.get('pager').total,
    };
    this.updatePagerState(pager, false);

    const sortParamData = params['sort'];
    if (sortParamData) {
      const sortCriteria = JSON.parse(sortParamData) as ISortCriteria;
      if (sortCriteria) {
        this.updateSortCriteriaState(sortCriteria);
      }
    } else {
      this.resetSortCriteriaState();
    }
  }

  private resetPagerState(): void {
    this.store.updateField('pager', this.storeInitialState.pager);
  }

  private resetFilterState(): void {
    this.store.updateField('filter', this.storeInitialState.filter);
  }

  private resetSortCriteriaState(): void {
    this.store.updateField('sortCriteria', this.storeInitialState.sortCriteria);
  }

  private updateSortCriteriaState(value: Partial<ISortCriteria>): void {
    if (!value.column) {
      return;
    }
    const sortState = this.store.get('sortCriteria');
    const newState = sortState ? { ...sortState, ...value } : { ...value };
    this.store.updateField('sortCriteria', newState as ISortCriteria);
  }

  private updateQueryParams(): void {
    const queryParams = {};

    const filterParam = this.store$.get('filter');
    if (filterParam) {
      const filters = Object.keys(filterParam).reduce((obj, key) => {
        if (filterParam[key]) {
          // eslint-disable-next-line functional/immutable-data
          obj[key] = filterParam[key] || null;
        }
        return obj;
      }, {});
      if (Object.keys(filters).length) {
        // eslint-disable-next-line functional/immutable-data
        queryParams['filter'] = JSON.stringify(filters);
      }
    }

    const countParam = this.store$.get('pager').count;
    if (countParam && countParam !== this.storeInitialState.pager.count) {
      // eslint-disable-next-line functional/immutable-data
      queryParams['count'] = countParam;
    }

    const pageParam = this.store$.get('pager').page;
    if (pageParam && pageParam !== this.storeInitialState.pager.page) {
      // eslint-disable-next-line functional/immutable-data
      queryParams['page'] = pageParam;
    }

    const sortParam = this.store$.get('sortCriteria');
    if (sortParam && sortParam.sort !== '') {
      // eslint-disable-next-line functional/immutable-data
      queryParams['sort'] = JSON.stringify(sortParam);
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
    });
  }
}
